import React, { useState } from 'react';

import { Formik, Form } from 'formik';
import { useRouter } from 'next/router';

import Button from '@components/common/Button/Button';
import PasswordInput from '@components/common/Formik/FormikPaswordInputField';
import useTranslateLpc from '@components/common/hooks/useTranslateLpc';
import NotificationBloc from '@components/common/NotificationBloc/NotificationBloc';
import Text from '@components/common/Text/Text';
import AuthenticationPageContext from '@components/lpc/Authentication/AuthenticationPageContext';
import Auth from '@services/domain/Lpc/AuthService';
import { getRoute, ROUTE } from '@services/http/Route';
import { passwordForgottenSubmitNewPassword } from '@validations/userSchema';

import css from './UpdatePassword.module.scss';

type UpdatePasswordProps = {
    uniqUP: string;
    setError?: any;
};

const ChangePasswordForm = ({ uniqUP, setError }: UpdatePasswordProps) => {
    const { t } = useTranslateLpc('lpc-authentication');
    const [isLoading, setIsLoading] = useState(false);
    const router = useRouter();

    const getInitialValues = () => {
        return {
            password: '',
            passwordConfirmation: '',
        };
    };

    return (
        <AuthenticationPageContext.Consumer>
            {({ setPage, pages }) => (
                <Formik
                    validationSchema={passwordForgottenSubmitNewPassword}
                    initialValues={getInitialValues()}
                    onSubmit={async (values) => {
                        setIsLoading(true);
                        Auth.passwordChangeConfirm({
                            password: values.password,
                            confirmPassword: values.passwordConfirmation,
                            uniq: uniqUP,
                        })
                            .then(() => setPage(pages.updatePasswordConfirmation))
                            .catch((err) => setError(err))
                            .finally(() => setIsLoading(false));
                    }}
                >
                    {() => (
                        <Form className={css.form}>
                            <div className={css.input}>
                                <PasswordInput
                                    name="password"
                                    type="password"
                                    isFloatingLabel
                                    label={t('change-password.new-password-label')}
                                    iconPosition="right"
                                />
                                <div className={css.input__tooltip}>
                                    <Text variant={'caption_02'} color={'txt-secondary'}>
                                        {t('sign-up.step-1.password-assisted-txt')}
                                    </Text>
                                </div>
                            </div>
                            <div className={css.input}>
                                <PasswordInput
                                    isFloatingLabel
                                    name="passwordConfirmation"
                                    type="password"
                                    label={t('change-password.confirm-new-password-label')}
                                    iconPosition="right"
                                />
                            </div>
                            <div className={css.actions}>
                                <Button
                                    width={'100%'}
                                    variant="primary"
                                    size="large"
                                    type="submit"
                                    isLoading={isLoading}
                                    isDisabled={isLoading}
                                >
                                    {t('change-password.modify')}
                                </Button>
                                <Button
                                    type={'button'}
                                    mobileFullWidth={true}
                                    variant="tertiary"
                                    size="large"
                                    onClick={() => router.push(getRoute(ROUTE.LPC.SIGN_IN))}
                                >
                                    {t('change-password.back')}
                                </Button>
                            </div>
                        </Form>
                    )}
                </Formik>
            )}
        </AuthenticationPageContext.Consumer>
    );
};

const ChangePassword = ({ uniqUP }: UpdatePasswordProps) => {
    const { t } = useTranslateLpc('lpc-authentication');
    const [error, setError] = useState(false);

    return (
        <div className={css['change-password']}>
            <h3>{t('change-password.title')}</h3>

            <div className={css.details}>
                <Text variant="body_02" color="ui-secondary">
                    {t('change-password.details')}
                </Text>
            </div>

            {error && (
                <NotificationBloc
                    customClass={css.notification}
                    onResetVisibility={(value) => setError(value)}
                    variant={'warning'}
                    message={t('change-password.must-be-new')}
                />
            )}

            <ChangePasswordForm uniqUP={uniqUP} setError={setError} />
        </div>
    );
};

export default ChangePassword;
